import { createFileRoute } from '@tanstack/react-router';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useOrganization, useSyncTenantFromParams } from '@/context/organization-context';
import { paths } from '@/routes/paths';
import { useEmailDocumentSectionsControllerGetOne } from 'src/apis/backend/generated/email-document-sections/email-document-sections';
import {
  EmailDocumentSectionEditor,
  type EmailDocumentSectionEditorVariant,
} from '@/sections/admin/email-document-sections/email-document-section-editor';
import { RawHtmlSectionChatEditor } from '@/sections/dashboard/sections/raw-html-section-chat-editor';
import { NewSectionModePicker } from '@/sections/dashboard/sections/new-section-mode-picker';

export const Route = createFileRoute('/_authed/dashboard_/o/$orgId/c/$clientId/sections/$id')({
  component: DashboardSectionEditorPage,
});

function DashboardSectionEditorPage() {
  const { id, orgId: routeOrgId, clientId: routeClientId } = Route.useParams();
  useSyncTenantFromParams(routeOrgId, routeClientId);
  const { activeOrganization, activeClient } = useOrganization();

  if (!activeOrganization || !activeClient) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Select an organization and client to edit sections.</Alert>
      </Box>
    );
  }

  const variant: EmailDocumentSectionEditorVariant = {
    kind: 'tenant',
    orgId: activeOrganization.id,
    clientId: activeClient.id,
    targetScope: 'client',
    backPath: paths.dashboard.sections,
    editPath: paths.dashboard.sectionEdit,
  };

  if (id === 'new') {
    return (
      <NewSectionModePicker
        orgId={activeOrganization.id}
        clientId={activeClient.id}
        backPath={paths.dashboard.sections}
        editPath={paths.dashboard.sectionEdit}
        renderBuildMode={() => <EmailDocumentSectionEditor variant={variant} />}
      />
    );
  }

  return (
    <SectionEditorRouter
      sectionId={id}
      orgId={activeOrganization.id}
      clientId={activeClient.id}
      variant={variant}
    />
  );
}

// Branches on the row's storage format. 'html' rows get the chat editor;
// 'node' rows fall through to the existing structured EmailBuilder editor.
// We do the fetch here (rather than in each editor) so the route stays the
// single source of truth for "which surface" and the editors don't need to
// re-derive it.
function SectionEditorRouter({
  sectionId,
  orgId,
  clientId,
  variant,
}: {
  sectionId: string;
  orgId: string;
  clientId: string;
  variant: EmailDocumentSectionEditorVariant;
}) {
  const { data, isLoading, error } = useEmailDocumentSectionsControllerGetOne(
    orgId,
    clientId,
    sectionId,
  );

  if (isLoading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load section: {(error as Error).message}
        </Alert>
      </Box>
    );
  }

  if (data?.sourceFormat === 'html') {
    return (
      <RawHtmlSectionChatEditor
        sectionId={sectionId}
        orgId={orgId}
        clientId={clientId}
        backPath={paths.dashboard.sections}
      />
    );
  }

  // Default: structured node editor (existing behavior).
  return <EmailDocumentSectionEditor sectionId={sectionId} variant={variant} />;
}
